import { useState, useMemo } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';
import { useParams, useNavigate } from 'react-router-dom';
import { dateRangeAtom } from '../state/dateRangeAtom';
import { botFilterAtom } from '../state/botFilterAtom';
import {
  Box,
  Heading,
  Card,
  CardBody,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Text,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Flex,
  Button,
  HStack,
  useColorModeValue,
  SimpleGrid,
  VStack,
  Icon,
  Badge,
  Progress,
  TableContainer,
  Spinner,
  Alert,
  AlertIcon,
  Skeleton,
  SkeletonText,
  Collapse,
  useToken,
  Tooltip,
  Input,
  InputGroup,
  InputLeftElement,
  useDisclosure,
} from '@chakra-ui/react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  Legend,
  PieChart,
  Pie,
  BarChart,
  Bar,
} from 'recharts';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faDownload,
  faChartLine,
  faGlobe,
  faMapMarkerAlt,
  faDesktop,
  faMobileAlt,
  faTabletAlt,
  faChevronDown,
  faChevronRight,
  faSearch,
  faSignInAlt,
  faSignOutAlt,
  faUsers,
  faUserClock,
  faClock,
  faChartPie,
  faChartBar,
  faInfoCircle,
} from '@fortawesome/free-solid-svg-icons';
import { ExportBuilderModal } from '../components/exports/ExportBuilderModal';
import {
  useTopPages,
  useTrafficSources,
  useDevices,
  useGeoData,
  useTrafficData,
  useAnalyticsOverview,
  useLandingPages,
  useExitPages,
  useStickiness,
  useHourlyActivity,
} from '../hooks/useAnalytics';
import { useChartAnimation } from '../hooks/useChartAnimation';
import DateRangePicker from '../components/common/DateRangePicker';
import { formatFullNumber, formatShare } from '../utils/format';
import { CHART_PALETTE, useChartColors, ChartPieTooltip } from '../components/common/chartTheme';

// Tab configuration for URL routing
const TAB_CONFIG = [
  { slug: 'overview', label: 'Overview' },
  { slug: 'pages', label: 'Pages' },
  { slug: 'landing-pages', label: 'Landing Pages' },
  { slug: 'exit-pages', label: 'Exit Pages' },
  { slug: 'sources', label: 'Sources' },
  { slug: 'geography', label: 'Geography' },
  { slug: 'devices', label: 'Devices' },
  { slug: 'engagement', label: 'Engagement' },
];

// Device type to icon mapping
const deviceIconMap = {
  desktop: faDesktop,
  mobile: faMobileAlt,
  tablet: faTabletAlt,
};

// Device type to color mapping
const deviceColorMap = {
  desktop: 'blue',
  mobile: 'purple',
  tablet: 'cyan',
};

// Source category color mapping
const sourceColorMap = {
  direct: 'green',
  referral: 'blue',
  social: 'purple',
  search: 'orange',
  email: 'cyan',
};

// SourceCategory component with expandable details
function SourceCategory({ source, hoverBg, borderColor, textSecondary }) {
  const [isOpen, setIsOpen] = useState(false);

  // For search type, we have sources (search engines) and keywords separately
  // For other types, we have details array
  const isSearch = source.type === 'search';
  const searchSources = isSearch ? (source.sources || []) : [];
  const searchKeywords = isSearch ? (source.keywords || []) : [];
  const details = !isSearch ? (source.details || []) : [];

  const hasExpandableContent = isSearch
    ? (searchSources.length > 0 || searchKeywords.length > 0)
    : details.length > 0;

  const color = sourceColorMap[source.type] || 'brand';

  // Build badge text
  let badgeText = '';
  if (isSearch) {
    const parts = [];
    if (searchSources.length > 0) parts.push(`${searchSources.length} engines`);
    if (searchKeywords.length > 0) parts.push(`${searchKeywords.length} keywords`);
    badgeText = parts.join(', ');
  } else if (details.length > 0) {
    badgeText = `${details.length} sources`;
  }

  return (
    <Box
      p={4}
      bg={hoverBg}
      borderRadius="xl"
      transition="all 0.2s"
    >
      <Flex
        justify="space-between"
        align="center"
        mb={2}
        cursor={hasExpandableContent ? 'pointer' : 'default'}
        onClick={() => hasExpandableContent && setIsOpen(!isOpen)}
        _hover={hasExpandableContent ? { opacity: 0.8 } : {}}
      >
        <HStack spacing={3}>
          {hasExpandableContent && (
            <Icon color={textSecondary} boxSize={3}>
              <FontAwesomeIcon icon={isOpen ? faChevronDown : faChevronRight} />
            </Icon>
          )}
          <Box
            w="8px"
            h="8px"
            borderRadius="full"
            bg={`${color}.500`}
          />
          <Text fontWeight="600">{source.name || 'Direct'}</Text>
          {badgeText && (
            <Badge colorScheme="gray" variant="subtle" borderRadius="full" fontSize="xs">
              {badgeText}
            </Badge>
          )}
        </HStack>
        <HStack spacing={4}>
          <Text fontSize="sm" color={textSecondary}>
            <Text as="span" fontWeight="600" color="inherit">
              {formatFullNumber(source.visitors || 0)}
            </Text> visitors
          </Text>
          <Badge colorScheme={color} variant="subtle" borderRadius="full">
            {source.percentage || 0}%
          </Badge>
        </HStack>
      </Flex>
      <Progress
        value={source.percentage || 0}
        size="sm"
        colorScheme={color}
        borderRadius="full"
        bg={borderColor}
      />
      {hasExpandableContent && (
        <Collapse in={isOpen} animateOpacity>
          <VStack spacing={2} align="stretch" mt={3} pl={6}>
            {/* For search: show search engines first */}
            {isSearch && searchSources.length > 0 && (
              <>
                <Text fontSize="xs" fontWeight="600" color={textSecondary} textTransform="uppercase">
                  Search Engines
                </Text>
                {searchSources.slice(0, 6).map((src, index) => (
                  <Flex key={`src-${index}`} justify="space-between" align="center" py={1}>
                    <Text fontSize="sm" color={textSecondary}>{src.name}</Text>
                    <HStack spacing={3}>
                      <Text fontSize="sm" fontWeight="500">
                        {formatFullNumber(src.visitors)} visitors
                      </Text>
                      <Text fontSize="xs" color={textSecondary}>
                        {formatFullNumber(src.visits)} visits
                      </Text>
                    </HStack>
                  </Flex>
                ))}
              </>
            )}
            {/* For search: show keywords */}
            {isSearch && searchKeywords.length > 0 && (
              <>
                <Text fontSize="xs" fontWeight="600" color={textSecondary} textTransform="uppercase" mt={searchSources.length > 0 ? 2 : 0}>
                  Keywords
                </Text>
                {searchKeywords.slice(0, 10).map((kw, index) => (
                  <Flex key={`kw-${index}`} justify="space-between" align="center" py={1}>
                    <Text fontSize="sm" color={textSecondary}>"{kw.name}"</Text>
                    <HStack spacing={3}>
                      <Text fontSize="sm" fontWeight="500">
                        {formatFullNumber(kw.visitors)} visitors
                      </Text>
                      <Text fontSize="xs" color={textSecondary}>
                        {formatFullNumber(kw.visits)} visits
                      </Text>
                    </HStack>
                  </Flex>
                ))}
                {searchKeywords.length > 10 && (
                  <Text fontSize="xs" color={textSecondary} textAlign="center">
                    +{searchKeywords.length - 10} more keywords
                  </Text>
                )}
              </>
            )}
            {/* For non-search: show details as before */}
            {!isSearch && details.slice(0, 10).map((detail, index) => (
              <Flex key={index} justify="space-between" align="center" py={1}>
                <Text fontSize="sm" color={textSecondary}>{detail.name}</Text>
                <HStack spacing={3}>
                  <Text fontSize="sm" fontWeight="500">
                    {formatFullNumber(detail.visitors)} visitors
                  </Text>
                  <Text fontSize="xs" color={textSecondary}>
                    {formatFullNumber(detail.visits)} visits
                  </Text>
                </HStack>
              </Flex>
            ))}
            {!isSearch && details.length > 10 && (
              <Text fontSize="xs" color={textSecondary} textAlign="center">
                +{details.length - 10} more
              </Text>
            )}
          </VStack>
        </Collapse>
      )}
    </Box>
  );
}

function AnalyticsPage() {
  const { tab } = useParams();
  const navigate = useNavigate();
  const [dateRange, setDateRange] = useRecoilState(dateRangeAtom);
  const filterBots = useRecoilValue(botFilterAtom);
  const [compareEnabled, setCompareEnabled] = useState(false);
  const [activeMetrics, setActiveMetrics] = useState(['visitors', 'pageviews', 'visits']);
  const { isOpen: isExportOpen, onOpen: onExportOpen, onClose: onExportClose } = useDisclosure();

  // Search and pagination state for overview cards
  const [pagesSearch, setPagesSearch] = useState('');
  const [pagesPage, setPagesPage] = useState(1);
  const ITEMS_PER_PAGE = 10;

  // Search and pagination state for tab reports
  const [pagesTabSearch, setPagesTabSearch] = useState('');
  const [pagesTabPage, setPagesTabPage] = useState(1);
  const [landingPagesSearch, setLandingPagesSearch] = useState('');
  const [exitPagesSearch, setExitPagesSearch] = useState('');
  const TAB_ITEMS_PER_PAGE = 20;

  // Get current tab index from URL param
  const tabIndex = useMemo(() => {
    const index = TAB_CONFIG.findIndex(t => t.slug === tab);
    return index >= 0 ? index : 0;
  }, [tab]);

  // Handle tab change - update URL
  const handleTabChange = (index) => {
    const newTab = TAB_CONFIG[index]?.slug || 'overview';
    navigate(`/analytics/${newTab}`, { replace: true });
  };

  const handleDateRangeChange = (range) => {
    setDateRange(range);
  };

  const toggleMetric = (metric) => {
    setActiveMetrics(prev =>
      prev.includes(metric)
        ? prev.filter(m => m !== metric)
        : [...prev, metric]
    );
  };

  const textSecondary = useColorModeValue('gray.500', 'gray.400');
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.100', 'gray.700');
  const hoverBg = useColorModeValue('gray.50', 'whiteAlpha.50');

  // Chart colors - using hex values for Recharts compatibility
  const { tooltipBg, tooltipText, gridColor, axisTextColor } = useChartColors();

  // Chart line/area colors
  const [brandColor, accentColor, cyanColor] = useToken('colors', ['brand.500', 'accent.500', 'cyan.500']);

  // Fetch data from API (with optional bot filtering)
  const { data: overviewData, loading: overviewLoading, error: overviewError } = useAnalyticsOverview(dateRange, false, filterBots);
  const { data: trafficData, loading: trafficLoading, error: trafficError } = useTrafficData(dateRange, 'day', false, filterBots);
  const { data: pagesData, loading: pagesLoading, error: pagesError } = useTopPages(dateRange, 100);
  const { data: sourcesData, loading: sourcesLoading, error: sourcesError } = useTrafficSources(dateRange);
  const { data: devicesData, loading: devicesLoading, error: devicesError } = useDevices(dateRange);
  const { data: geoData, loading: geoLoading, error: geoError } = useGeoData(dateRange);
  const { data: landingPagesData, loading: landingPagesLoading, error: landingPagesError } = useLandingPages(dateRange, 10);
  const { data: exitPagesData, loading: exitPagesLoading, error: exitPagesError } = useExitPages(dateRange, 10);
  const { data: stickinessData, loading: stickinessLoading, error: stickinessError } = useStickiness(dateRange);
  const { data: hourlyData, loading: hourlyLoading, error: hourlyError } = useHourlyActivity(dateRange);

  // Chart entrance animations (animate on first load / after idle, calm on refresh)
  const trendAnim = useChartAnimation(trafficLoading);
  const nvrAnim = useChartAnimation(overviewLoading);
  const sourcesAnim = useChartAnimation(sourcesLoading);
  const devicesAnim = useChartAnimation(devicesLoading);
  const hourlyAnim = useChartAnimation(hourlyLoading);

  // Filtered and paginated pages data
  const filteredPagesData = useMemo(() => {
    if (!pagesData) return [];
    if (!pagesSearch.trim()) return pagesData;
    return pagesData.filter(page =>
      page.path.toLowerCase().includes(pagesSearch.toLowerCase())
    );
  }, [pagesData, pagesSearch]);

  const paginatedPagesData = useMemo(() => {
    const start = (pagesPage - 1) * ITEMS_PER_PAGE;
    return filteredPagesData.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredPagesData, pagesPage]);

  const totalPagesPages = Math.ceil(filteredPagesData.length / ITEMS_PER_PAGE);

  // Reset pagination when search changes
  const handlePagesSearchChange = (value) => {
    setPagesSearch(value);
    setPagesPage(1);
  };

  // Filtered and paginated data for Pages tab
  const filteredPagesTabData = useMemo(() => {
    if (!pagesData) return [];
    if (!pagesTabSearch.trim()) return pagesData;
    return pagesData.filter(page =>
      page.path.toLowerCase().includes(pagesTabSearch.toLowerCase())
    );
  }, [pagesData, pagesTabSearch]);

  const paginatedPagesTabData = useMemo(() => {
    const start = (pagesTabPage - 1) * TAB_ITEMS_PER_PAGE;
    return filteredPagesTabData.slice(start, start + TAB_ITEMS_PER_PAGE);
  }, [filteredPagesTabData, pagesTabPage]);

  const totalPagesTabPages = Math.ceil(filteredPagesTabData.length / TAB_ITEMS_PER_PAGE);

  const handlePagesTabSearchChange = (value) => {
    setPagesTabSearch(value);
    setPagesTabPage(1);
  };

  // Helper to format duration in seconds to readable time
  const formatDuration = (seconds) => {
    if (!seconds) return '0s';
    if (seconds < 60) return `${seconds}s`;
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return secs > 0 ? `${mins}m ${secs}s` : `${mins}m`;
  };

  // Format traffic data for chart
  const trafficPreset = typeof dateRange === 'string' ? dateRange : dateRange?.preset;
  const isHourlyTraffic = trafficPreset === 'today' || trafficPreset === 'yesterday';
  const chartData = trafficData?.map(item => {
    const d = new Date(item.date);
    const dateLabel = isHourlyTraffic
      ? d.toLocaleTimeString('en-US', { hour: 'numeric', hour12: true })
      : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    return {
      date: dateLabel,
      visitors: item.visitors || 0,
      visits: item.visits || 0,
      pageviews: item.pageviews || 0,
    };
  }) || [];

  // Prepare data for New vs Returning donut chart
  const newVsReturningData = overviewData ? [
    { name: 'New Visitors', value: overviewData.newVisitors || 0, percentage: overviewData.newVisitorPercentage || 0, fill: '#10B981' },
    { name: 'Returning Visitors', value: overviewData.returningVisitors || 0, percentage: overviewData.returningVisitorPercentage || 0, fill: '#3B82F6' },
  ] : [];

  // Prepare data for Sources pie chart
  const sourcesPieData = sourcesData?.map((source, index) => ({
    name: source.name,
    value: source.visitors || 0,
    percentage: source.percentage || 0,
    fill: CHART_PALETTE[index % CHART_PALETTE.length],
  })) || [];

  // Extract URL path for display
  const extractPath = (url) => {
    if (!url) return '/';
    try {
      const urlObj = new URL(url);
      return urlObj.pathname || '/';
    } catch {
      return url;
    }
  };

  return (
    <Box>
      {/* Page Header */}
      <Flex
        direction={{ base: 'column', md: 'row' }}
        justify="space-between"
        align={{ base: 'flex-start', md: 'center' }}
        mb={{ base: 6, md: 8 }}
        gap={4}
      >
        <Box>
          <Heading
            size={{ base: 'lg', md: 'xl' }}
            bgGradient="linear(to-r, brand.500, accent.500)"
            bgClip="text"
            fontWeight="700"
          >
            Web Stats
          </Heading>
          <Text color={textSecondary} mt={1}>
            Detailed insights into your traffic and performance
          </Text>
        </Box>
        <Flex direction={{ base: 'column', xl: 'row' }} gap={3} align={{ base: 'flex-start', xl: 'center' }}>
          <DateRangePicker
            value={dateRange}
            onChange={handleDateRangeChange}
            showCompare={true}
            compareEnabled={compareEnabled}
            onCompareChange={setCompareEnabled}
            size="sm"
          />
          <Button
            leftIcon={<FontAwesomeIcon icon={faDownload} />}
            size="sm"
            variant="outline"
            borderRadius="xl"
            onClick={onExportOpen}
          >
            Export
          </Button>
        </Flex>
      </Flex>

      <Tabs colorScheme="brand" variant="soft-rounded" index={tabIndex} onChange={handleTabChange}>
        <TabList
          flexWrap="wrap"
          gap={2}
          bg={cardBg}
          p={2}
          borderRadius="2xl"
          border="1px solid"
          borderColor={borderColor}
          mb={6}
        >
          <Tab borderRadius="xl" fontSize="sm">Overview</Tab>
          <Tab borderRadius="xl" fontSize="sm">Pages</Tab>
          <Tab borderRadius="xl" fontSize="sm">Landing Pages</Tab>
          <Tab borderRadius="xl" fontSize="sm">Exit Pages</Tab>
          <Tab borderRadius="xl" fontSize="sm">Sources</Tab>
          <Tab borderRadius="xl" fontSize="sm">Geography</Tab>
          <Tab borderRadius="xl" fontSize="sm">Devices</Tab>
          <Tab borderRadius="xl" fontSize="sm">Engagement</Tab>
        </TabList>

        <TabPanels>
          {/* Overview Tab */}
          <TabPanel px={0}>
            {/* Key Metrics Cards - Row 1 */}
            <SimpleGrid columns={{ base: 2, md: 4, lg: 8 }} spacing={4} mb={6}>
              {overviewLoading ? (
                <>
                  {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
                    <Card key={i} bg={cardBg} border="1px solid" borderColor={borderColor}>
                      <CardBody py={3} px={3}>
                        <Skeleton height="14px" width="60%" mb={2} />
                        <Skeleton height="24px" width="80%" />
                      </CardBody>
                    </Card>
                  ))}
                </>
              ) : overviewError ? (
                <Card bg={cardBg} border="1px solid" borderColor={borderColor} gridColumn="span 8">
                  <CardBody>
                    <Alert status="error" borderRadius="lg">
                      <AlertIcon />
                      {overviewError}
                    </Alert>
                  </CardBody>
                </Card>
              ) : (
                <>
                  <Card bg={cardBg} border="1px solid" borderColor={borderColor}>
                    <CardBody py={3} px={3}>
                      <Text fontSize="xs" color={textSecondary} fontWeight="500" textTransform="uppercase" mb={1}>
                        Unique Visitors
                      </Text>
                      <Text fontSize="xl" fontWeight="700" bgGradient="linear(to-r, brand.500, accent.500)" bgClip="text">
                        {formatFullNumber(overviewData?.uniqueVisitors)}
                      </Text>
                    </CardBody>
                  </Card>
                  <Card bg={cardBg} border="1px solid" borderColor={borderColor}>
                    <CardBody py={3} px={3}>
                      <Text fontSize="xs" color={textSecondary} fontWeight="500" textTransform="uppercase" mb={1}>
                        Sessions
                        <Tooltip label="A session (visit) is a group of user interactions within a time frame. Same as 'Visits' in Matomo." placement="top">
                          <span style={{ marginLeft: '4px', cursor: 'help' }}>
                            <FontAwesomeIcon icon={faInfoCircle} size="xs" />
                          </span>
                        </Tooltip>
                      </Text>
                      <Text fontSize="xl" fontWeight="700" color="cyan.500">
                        {formatFullNumber(overviewData?.totalVisits)}
                      </Text>
                    </CardBody>
                  </Card>
                  <Card bg={cardBg} border="1px solid" borderColor={borderColor}>
                    <CardBody py={3} px={3}>
                      <Text fontSize="xs" color={textSecondary} fontWeight="500" textTransform="uppercase" mb={1}>
                        Page Views
                      </Text>
                      <Text fontSize="xl" fontWeight="700" color="purple.500">
                        {formatFullNumber(overviewData?.totalPageviews)}
                      </Text>
                    </CardBody>
                  </Card>
                  <Card bg={cardBg} border="1px solid" borderColor={borderColor}>
                    <CardBody py={3} px={3}>
                      <Text fontSize="xs" color={textSecondary} fontWeight="500" textTransform="uppercase" mb={1}>
                        Sessions/User
                      </Text>
                      <Text fontSize="xl" fontWeight="700" color="blue.500">
                        {overviewData?.sessionsPerUser || 0}
                      </Text>
                    </CardBody>
                  </Card>
                  <Card bg={cardBg} border="1px solid" borderColor={borderColor}>
                    <CardBody py={3} px={3}>
                      <Text fontSize="xs" color={textSecondary} fontWeight="500" textTransform="uppercase" mb={1}>
                        Pages/Session
                      </Text>
                      <Text fontSize="xl" fontWeight="700" color="teal.500">
                        {overviewData?.pagesPerSession || 0}
                      </Text>
                    </CardBody>
                  </Card>
                  <Card bg={cardBg} border="1px solid" borderColor={borderColor}>
                    <CardBody py={3} px={3}>
                      <Text fontSize="xs" color={textSecondary} fontWeight="500" textTransform="uppercase" mb={1}>
                        Avg. Session
                      </Text>
                      <Text fontSize="xl" fontWeight="700" color="orange.500">
                        {formatDuration(overviewData?.avgSessionDuration)}
                      </Text>
                    </CardBody>
                  </Card>
                  <Card bg={cardBg} border="1px solid" borderColor={borderColor}>
                    <CardBody py={3} px={3}>
                      <Text fontSize="xs" color={textSecondary} fontWeight="500" textTransform="uppercase" mb={1}>
                        Bounce Rate
                      </Text>
                      <Text fontSize="xl" fontWeight="700" color={overviewData?.bounceRate > 50 ? 'red.500' : 'green.500'}>
                        {overviewData?.bounceRate || 0}%
                      </Text>
                    </CardBody>
                  </Card>
                  <Card bg={cardBg} border="1px solid" borderColor={borderColor}>
                    <CardBody py={3} px={3}>
                      <Text fontSize="xs" color={textSecondary} fontWeight="500" textTransform="uppercase" mb={1}>
                        Engagement Rate
                        <Tooltip label="Percentage of sessions that were not bounces (engaged with multiple pages)" placement="top">
                          <span style={{ marginLeft: '4px', cursor: 'help' }}>
                            <FontAwesomeIcon icon={faInfoCircle} size="xs" />
                          </span>
                        </Tooltip>
                      </Text>
                      <Text fontSize="xl" fontWeight="700" color={overviewData?.engagementRate > 50 ? 'green.500' : 'orange.500'}>
                        {overviewData?.engagementRate || 0}%
                      </Text>
                    </CardBody>
                  </Card>
                </>
              )}
            </SimpleGrid>

            {/* Traffic Chart and New vs Returning */}
            <SimpleGrid columns={{ base: 1, lg: 3 }} spacing={6} mb={6}>
              {/* Traffic Over Time Chart */}
              <Card bg={cardBg} border="1px solid" borderColor={borderColor} gridColumn={{ lg: 'span 2' }}>
                <CardBody>
                  <Flex justify="space-between" align="center" mb={4}>
                    <HStack spacing={2}>
                      <Box p={2} bgGradient="linear(135deg, brand.500 0%, accent.500 100%)" borderRadius="lg">
                        <Icon color="white" boxSize={4}>
                          <FontAwesomeIcon icon={faChartLine} />
                        </Icon>
                      </Box>
                      <Heading size="md">Traffic Over Time</Heading>
                    </HStack>
                    {trafficLoading ? (
                      <Spinner size="sm" color="brand.500" />
                    ) : (
                      <Badge colorScheme="green" variant="subtle" borderRadius="full">Live Data</Badge>
                    )}
                  </Flex>
                  {trafficError ? (
                    <Alert status="error" borderRadius="xl">
                      <AlertIcon />
                      {trafficError}
                    </Alert>
                  ) : trafficLoading ? (
                    <Skeleton h="300px" borderRadius="xl" />
                  ) : chartData.length > 0 ? (
                    <Box>
                      <HStack spacing={2} mb={4} flexWrap="wrap">
                        <Text fontSize="sm" color={textSecondary} mr={2}>Show:</Text>
                        <Button
                          size="sm"
                          variant={activeMetrics.includes('visitors') ? 'solid' : 'outline'}
                          colorScheme="brand"
                          borderRadius="full"
                          onClick={() => toggleMetric('visitors')}
                          bgGradient={activeMetrics.includes('visitors') ? 'linear(to-r, brand.500, accent.500)' : undefined}
                          _hover={{
                            bgGradient: activeMetrics.includes('visitors') ? 'linear(to-r, brand.600, accent.600)' : undefined,
                          }}
                          leftIcon={<Box w={2} h={2} borderRadius="full" bg={activeMetrics.includes('visitors') ? 'white' : 'brand.500'} />}
                        >
                          Visitors
                        </Button>
                        <Button
                          size="sm"
                          variant={activeMetrics.includes('pageviews') ? 'solid' : 'outline'}
                          colorScheme="pink"
                          borderRadius="full"
                          onClick={() => toggleMetric('pageviews')}
                          bgGradient={activeMetrics.includes('pageviews') ? 'linear(to-r, pink.500, purple.500)' : undefined}
                          _hover={{
                            bgGradient: activeMetrics.includes('pageviews') ? 'linear(to-r, pink.600, purple.600)' : undefined,
                          }}
                          leftIcon={<Box w={2} h={2} borderRadius="full" bg={activeMetrics.includes('pageviews') ? 'white' : 'pink.500'} />}
                        >
                          Pageviews
                        </Button>
                        <Button
                          size="sm"
                          variant={activeMetrics.includes('visits') ? 'solid' : 'outline'}
                          colorScheme="cyan"
                          borderRadius="full"
                          onClick={() => toggleMetric('visits')}
                          bgGradient={activeMetrics.includes('visits') ? 'linear(to-r, cyan.500, blue.500)' : undefined}
                          _hover={{
                            bgGradient: activeMetrics.includes('visits') ? 'linear(to-r, cyan.600, blue.600)' : undefined,
                          }}
                          leftIcon={<Box w={2} h={2} borderRadius="full" bg={activeMetrics.includes('visits') ? 'white' : 'cyan.500'} />}
                        >
                          Sessions
                        </Button>
                      </HStack>
                      <Box h="300px">
                        <ResponsiveContainer width="100%" height="100%">
                          <AreaChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                            <defs>
                              <linearGradient id="colorVisitors" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor={brandColor} stopOpacity={0.3} />
                                <stop offset="95%" stopColor={brandColor} stopOpacity={0} />
                              </linearGradient>
                              <linearGradient id="colorPageviews" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor={accentColor} stopOpacity={0.3} />
                                <stop offset="95%" stopColor={accentColor} stopOpacity={0} />
                              </linearGradient>
                              <linearGradient id="colorVisits" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor={cyanColor} stopOpacity={0.3} />
                                <stop offset="95%" stopColor={cyanColor} stopOpacity={0} />
                              </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
                            <XAxis dataKey="date" tick={{ fontSize: 12, fill: axisTextColor }} axisLine={{ stroke: gridColor }} tickLine={{ stroke: gridColor }} />
                            <YAxis tick={{ fontSize: 12, fill: axisTextColor }} axisLine={{ stroke: gridColor }} tickLine={{ stroke: gridColor }} />
                            <RechartsTooltip contentStyle={{ backgroundColor: tooltipBg, color: tooltipText, border: `1px solid ${gridColor}`, borderRadius: '8px' }} />
                            <Legend wrapperStyle={{ color: axisTextColor }} />
                            {activeMetrics.includes('visitors') && (
                              <Area {...trendAnim} type="monotone" dataKey="visitors" name="Visitors" stroke={brandColor} strokeWidth={2} fillOpacity={1} fill="url(#colorVisitors)" />
                            )}
                            {activeMetrics.includes('pageviews') && (
                              <Area {...trendAnim} type="monotone" dataKey="pageviews" name="Pageviews" stroke={accentColor} strokeWidth={2} fillOpacity={1} fill="url(#colorPageviews)" />
                            )}
                            {activeMetrics.includes('visits') && (
                              <Area {...trendAnim} type="monotone" dataKey="visits" name="Sessions" stroke={cyanColor} strokeWidth={2} fillOpacity={1} fill="url(#colorVisits)" />
                            )}
                          </AreaChart>
                        </ResponsiveContainer>
                      </Box>
                    </Box>
                  ) : (
                    <Box h="300px" display="flex" alignItems="center" justifyContent="center" bg={hoverBg} borderRadius="xl" border="2px dashed" borderColor={borderColor}>
                      <VStack spacing={2}>
                        <Icon color={textSecondary} boxSize={8}>
                          <FontAwesomeIcon icon={faChartLine} />
                        </Icon>
                        <Text color={textSecondary} fontWeight="500">No traffic data available</Text>
                      </VStack>
                    </Box>
                  )}
                </CardBody>
              </Card>

              {/* New vs Returning Donut Chart */}
              <Card bg={cardBg} border="1px solid" borderColor={borderColor}>
                <CardBody>
                  <Flex justify="space-between" align="center" mb={4}>
                    <HStack spacing={2}>
                      <Box p={2} bgGradient="linear(135deg, green.500 0%, blue.500 100%)" borderRadius="lg">
                        <Icon color="white" boxSize={4}>
                          <FontAwesomeIcon icon={faUsers} />
                        </Icon>
                      </Box>
                      <Heading size="md">New vs Returning</Heading>
                    </HStack>
                  </Flex>
                  {overviewLoading ? (
                    <Skeleton h="250px" borderRadius="xl" />
                  ) : newVsReturningData.length > 0 && (newVsReturningData[0].value > 0 || newVsReturningData[1].value > 0) ? (
                    <Box>
                      <Box h="200px">
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart>
                            <Pie
                              {...nvrAnim}
                              data={newVsReturningData}
                              cx="50%"
                              cy="50%"
                              innerRadius={50}
                              outerRadius={80}
                              paddingAngle={2}
                              dataKey="value"
                            />
                            <RechartsTooltip content={<ChartPieTooltip />} />
                          </PieChart>
                        </ResponsiveContainer>
                      </Box>
                      <VStack spacing={2} mt={2}>
                        <Flex w="100%" justify="space-between" align="center">
                          <HStack>
                            <Box w={3} h={3} borderRadius="full" bg="green.500" />
                            <Text fontSize="sm">New Visitors</Text>
                          </HStack>
                          <HStack>
                            <Text fontWeight="600">{formatFullNumber(overviewData?.newVisitors)}</Text>
                            <Badge colorScheme="green" variant="subtle" borderRadius="full">{overviewData?.newVisitorPercentage}%</Badge>
                          </HStack>
                        </Flex>
                        <Flex w="100%" justify="space-between" align="center">
                          <HStack>
                            <Box w={3} h={3} borderRadius="full" bg="blue.500" />
                            <Text fontSize="sm">Returning</Text>
                          </HStack>
                          <HStack>
                            <Text fontWeight="600">{formatFullNumber(overviewData?.returningVisitors)}</Text>
                            <Badge colorScheme="blue" variant="subtle" borderRadius="full">{overviewData?.returningVisitorPercentage}%</Badge>
                          </HStack>
                        </Flex>
                      </VStack>
                    </Box>
                  ) : (
                    <Box h="250px" display="flex" alignItems="center" justifyContent="center" bg={hoverBg} borderRadius="xl">
                      <Text color={textSecondary}>No visitor data available</Text>
                    </Box>
                  )}
                </CardBody>
              </Card>
            </SimpleGrid>

            {/* Sources and Top Pages - 2 column layout */}
            <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6} mb={6}>
              {/* Traffic Sources Pie Chart */}
              <Card bg={cardBg} border="1px solid" borderColor={borderColor}>
                <CardBody>
                  <Flex justify="space-between" align="center" mb={4}>
                    <HStack spacing={2}>
                      <Box p={2} bgGradient="linear(135deg, purple.500 0%, pink.500 100%)" borderRadius="lg">
                        <Icon color="white" boxSize={4}>
                          <FontAwesomeIcon icon={faChartPie} />
                        </Icon>
                      </Box>
                      <Heading size="sm">Traffic Sources</Heading>
                    </HStack>
                    {sourcesLoading && <Spinner size="sm" color="brand.500" />}
                  </Flex>
                  {sourcesError ? (
                    <Alert status="error" borderRadius="xl" size="sm">
                      <AlertIcon />
                      {sourcesError}
                    </Alert>
                  ) : sourcesLoading ? (
                    <Skeleton h="200px" borderRadius="xl" />
                  ) : sourcesPieData.length > 0 ? (
                    <Box>
                      <Box h="150px">
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart>
                            <Pie
                              {...sourcesAnim}
                              data={sourcesPieData}
                              cx="50%"
                              cy="50%"
                              outerRadius={60}
                              paddingAngle={2}
                              dataKey="value"
                            />
                            <RechartsTooltip content={<ChartPieTooltip />} />
                          </PieChart>
                        </ResponsiveContainer>
                      </Box>
                      <VStack spacing={1} mt={2}>
                        {sourcesPieData.map((source, index) => (
                          <Flex key={source.name} w="100%" justify="space-between" align="center">
                            <HStack>
                              <Box w={2} h={2} borderRadius="full" bg={CHART_PALETTE[index % CHART_PALETTE.length]} />
                              <Text fontSize="xs">{source.name}</Text>
                            </HStack>
                            <HStack>
                              <Text fontWeight="600" fontSize="xs">{formatFullNumber(source.value)}</Text>
                              <Badge colorScheme="gray" variant="subtle" borderRadius="full" fontSize="xs">{formatShare(source.percentage, source.value)}</Badge>
                            </HStack>
                          </Flex>
                        ))}
                      </VStack>
                    </Box>
                  ) : (
                    <Box h="200px" display="flex" alignItems="center" justifyContent="center" bg={hoverBg} borderRadius="xl">
                      <Text color={textSecondary} fontSize="sm">No source data available</Text>
                    </Box>
                  )}
                </CardBody>
              </Card>

              {/* Top Pages with Search and Pagination */}
              <Card bg={cardBg} border="1px solid" borderColor={borderColor}>
                <CardBody>
                  <Flex justify="space-between" align="center" mb={3}>
                    <HStack spacing={2}>
                      <Box p={2} bgGradient="linear(135deg, cyan.500 0%, blue.500 100%)" borderRadius="lg">
                        <Icon color="white" boxSize={4}>
                          <FontAwesomeIcon icon={faChartBar} />
                        </Icon>
                      </Box>
                      <Heading size="sm">Top Pages</Heading>
                    </HStack>
                    {pagesLoading && <Spinner size="sm" color="brand.500" />}
                  </Flex>
                  {/* Search */}
                  <InputGroup size="sm" mb={3}>
                    <InputLeftElement>
                      <Icon color={textSecondary} boxSize={3}>
                        <FontAwesomeIcon icon={faSearch} />
                      </Icon>
                    </InputLeftElement>
                    <Input
                      placeholder="Search pages..."
                      value={pagesSearch}
                      onChange={(e) => handlePagesSearchChange(e.target.value)}
                      borderRadius="lg"
                    />
                  </InputGroup>
                  {pagesError ? (
                    <Alert status="error" borderRadius="xl" size="sm">
                      <AlertIcon />
                      {pagesError}
                    </Alert>
                  ) : pagesLoading ? (
                    <VStack spacing={2}>
                      {[1, 2, 3, 4, 5].map(i => <Skeleton key={i} h="28px" w="100%" borderRadius="md" />)}
                    </VStack>
                  ) : paginatedPagesData.length > 0 ? (
                    <>
                      <VStack spacing={1} align="stretch" maxH="280px" overflowY="auto">
                        {paginatedPagesData.map((page, index) => (
                          <Flex key={page.path} justify="space-between" align="center" p={2} bg={hoverBg} borderRadius="lg">
                            <HStack spacing={2} flex={1} minW={0}>
                              <Badge colorScheme="brand" variant="subtle" borderRadius="full" minW="20px" fontSize="xs" textAlign="center">
                                {(pagesPage - 1) * ITEMS_PER_PAGE + index + 1}
                              </Badge>
                              <Text fontSize="xs" fontWeight="500" isTruncated title={page.path}>
                                {page.path}
                              </Text>
                            </HStack>
                            <HStack spacing={2}>
                              <Text fontSize="xs" fontWeight="600">{formatFullNumber(page.views)}</Text>
                              {page.avgTime > 0 && (
                                <Text fontSize="xs" color={textSecondary}>{formatDuration(page.avgTime)}</Text>
                              )}
                              <Badge colorScheme="gray" variant="subtle" borderRadius="full" fontSize="xs">
                                {formatShare(page.percentage, page.views)}
                              </Badge>
                            </HStack>
                          </Flex>
                        ))}
                      </VStack>
                      {/* Pagination */}
                      {totalPagesPages > 1 && (
                        <Flex justify="space-between" align="center" mt={3} pt={2} borderTop="1px solid" borderColor={borderColor}>
                          <Text fontSize="xs" color={textSecondary}>
                            {filteredPagesData.length} pages
                          </Text>
                          <HStack spacing={1}>
                            <Button
                              size="xs"
                              variant="ghost"
                              onClick={() => setPagesPage(p => Math.max(1, p - 1))}
                              isDisabled={pagesPage === 1}
                            >
                              Prev
                            </Button>
                            <Text fontSize="xs" color={textSecondary}>
                              {pagesPage}/{totalPagesPages}
                            </Text>
                            <Button
                              size="xs"
                              variant="ghost"
                              onClick={() => setPagesPage(p => Math.min(totalPagesPages, p + 1))}
                              isDisabled={pagesPage === totalPagesPages}
                            >
                              Next
                            </Button>
                          </HStack>
                        </Flex>
                      )}
                    </>
                  ) : (
                    <Box h="150px" display="flex" alignItems="center" justifyContent="center" bg={hoverBg} borderRadius="xl">
                      <Text color={textSecondary} fontSize="sm">
                        {pagesSearch ? 'No matching pages' : 'No page data available'}
                      </Text>
                    </Box>
                  )}
                </CardBody>
              </Card>
            </SimpleGrid>

            {/* Quick Devices and Top Countries */}
            <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6}>
              {/* Quick Device Breakdown */}
              <Card bg={cardBg} border="1px solid" borderColor={borderColor}>
                <CardBody>
                  <Flex justify="space-between" align="center" mb={4}>
                    <HStack spacing={2}>
                      <Box p={2} bgGradient="linear(135deg, purple.500 0%, pink.500 100%)" borderRadius="lg">
                        <Icon color="white" boxSize={4}>
                          <FontAwesomeIcon icon={faDesktop} />
                        </Icon>
                      </Box>
                      <Heading size="sm">Device Breakdown</Heading>
                    </HStack>
                    {devicesLoading && <Spinner size="sm" color="brand.500" />}
                  </Flex>
                  {devicesError ? (
                    <Alert status="error" borderRadius="xl" size="sm">
                      <AlertIcon />
                      {devicesError}
                    </Alert>
                  ) : devicesLoading ? (
                    <Skeleton h="120px" borderRadius="xl" />
                  ) : devicesData && devicesData.length > 0 ? (
                    <Flex align="center" justify="space-between">
                      <Box w="120px" h="120px">
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart>
                            <Pie
                              {...devicesAnim}
                              data={devicesData.map((d, index) => {
                                const type = d.device || d.device_type || d.type || 'desktop';
                                return { name: type.charAt(0).toUpperCase() + type.slice(1), value: d.visitors || d.unique_visitors || 0, fill: CHART_PALETTE[index % CHART_PALETTE.length] };
                              })}
                              cx="50%"
                              cy="50%"
                              innerRadius={35}
                              outerRadius={55}
                              paddingAngle={2}
                              dataKey="value"
                            />
                            <RechartsTooltip content={<ChartPieTooltip />} />
                          </PieChart>
                        </ResponsiveContainer>
                      </Box>
                      <VStack spacing={2} align="stretch" flex={1} ml={4}>
                        {devicesData.map((device, index) => {
                          const deviceType = (device.device || device.device_type || device.type || 'desktop').toLowerCase();
                          const icon = deviceIconMap[deviceType] || faDesktop;
                          const colors = ['#6366f1', '#ec4899', '#06b6d4'];
                          return (
                            <Flex key={deviceType} justify="space-between" align="center">
                              <HStack spacing={2}>
                                <Box w="8px" h="8px" borderRadius="full" bg={colors[index % 3]} />
                                <Icon color={textSecondary} boxSize={3}>
                                  <FontAwesomeIcon icon={icon} />
                                </Icon>
                                <Text fontSize="sm" textTransform="capitalize">{deviceType}</Text>
                              </HStack>
                              <HStack spacing={2}>
                                <Text fontSize="sm" fontWeight="600">{formatShare(device.percentage || device.percent, device.visitors || device.unique_visitors)}</Text>
                              </HStack>
                            </Flex>
                          );
                        })}
                      </VStack>
                    </Flex>
                  ) : (
                    <Box h="180px" display="flex" alignItems="center" justifyContent="center" bg={hoverBg} borderRadius="xl">
                      <Text color={textSecondary} fontSize="sm">No device data available</Text>
                    </Box>
                  )}
                </CardBody>
              </Card>

              {/* Top Countries */}
              <Card bg={cardBg} border="1px solid" borderColor={borderColor}>
                <CardBody>
                  <Flex justify="space-between" align="center" mb={4}>
                    <HStack spacing={2}>
                      <Box p={2} bgGradient="linear(135deg, green.500 0%, teal.500 100%)" borderRadius="lg">
                        <Icon color="white" boxSize={4}>
                          <FontAwesomeIcon icon={faMapMarkerAlt} />
                        </Icon>
                      </Box>
                      <Heading size="sm">Top Countries</Heading>
                    </HStack>
                    {geoLoading && <Spinner size="sm" color="brand.500" />}
                  </Flex>
                  {geoError ? (
                    <Alert status="error" borderRadius="xl" size="sm">
                      <AlertIcon />
                      {geoError}
                    </Alert>
                  ) : geoLoading ? (
                    <VStack spacing={2}>
                      {[1, 2, 3, 4, 5].map(i => <Skeleton key={i} h="32px" w="100%" borderRadius="md" />)}
                    </VStack>
                  ) : geoData && geoData.length > 0 ? (
                    <VStack spacing={2} align="stretch">
                      {geoData.slice(0, 5).map((country, index) => (
                        <Box key={country.country || country.country_code || index}>
                          <Flex justify="space-between" align="center" mb={1}>
                            <HStack spacing={2}>
                              <Text fontSize="lg">
                                {country.flag || (country.country_code ? String.fromCodePoint(...[...country.country_code.toUpperCase()].map(c => 127397 + c.charCodeAt(0))) : '')}
                              </Text>
                              <Text fontSize="sm" fontWeight="500">{country.country || country.name || 'Unknown'}</Text>
                            </HStack>
                            <HStack spacing={2}>
                              <Text fontSize="sm" color={textSecondary}>{formatFullNumber(country.visitors || country.unique_visitors || 0)}</Text>
                              <Badge colorScheme="green" variant="subtle" borderRadius="full" fontSize="xs">
                                {country.percentage || country.percent || 0}%
                              </Badge>
                            </HStack>
                          </Flex>
                          <Progress
                            value={country.percentage || country.percent || 0}
                            size="xs"
                            colorScheme="green"
                            borderRadius="full"
                            bg={borderColor}
                          />
                        </Box>
                      ))}
                    </VStack>
                  ) : (
                    <Box h="180px" display="flex" alignItems="center" justifyContent="center" bg={hoverBg} borderRadius="xl">
                      <Text color={textSecondary} fontSize="sm">No geographic data available</Text>
                    </Box>
                  )}
                </CardBody>
              </Card>
            </SimpleGrid>
          </TabPanel>

          {/* Pages Tab */}
          <TabPanel px={0}>
            <Card bg={cardBg} border="1px solid" borderColor={borderColor}>
              <CardBody p={{ base: 4, md: 6 }}>
                <Flex justify="space-between" align="center" mb={4} flexWrap="wrap" gap={3}>
                  <HStack spacing={2}>
                    <Box p={2} bgGradient="linear(135deg, purple.500 0%, pink.500 100%)" borderRadius="lg">
                      <Icon color="white" boxSize={4}>
                        <FontAwesomeIcon icon={faChartBar} />
                      </Icon>
                    </Box>
                    <Box>
                      <Heading size="md">All Pages</Heading>
                      <Text fontSize="sm" color={textSecondary}>Performance metrics for all tracked pages</Text>
                    </Box>
                  </HStack>
                  <HStack spacing={3}>
                    <InputGroup size="sm" maxW="250px">
                      <InputLeftElement>
                        <Icon color={textSecondary} boxSize={3}>
                          <FontAwesomeIcon icon={faSearch} />
                        </Icon>
                      </InputLeftElement>
                      <Input
                        placeholder="Search pages..."
                        value={pagesTabSearch}
                        onChange={(e) => handlePagesTabSearchChange(e.target.value)}
                        borderRadius="lg"
                      />
                    </InputGroup>
                    {filteredPagesTabData.length > 0 && (
                      <Text fontSize="sm" color={textSecondary} whiteSpace="nowrap">
                        {filteredPagesTabData.length} pages
                      </Text>
                    )}
                  </HStack>
                </Flex>
                {pagesError ? (
                  <Alert status="error" borderRadius="xl">
                    <AlertIcon />
                    {pagesError}
                  </Alert>
                ) : pagesLoading ? (
                  <VStack spacing={4} align="stretch">
                    {[1, 2, 3, 4].map((i) => (
                      <Skeleton key={i} height="40px" borderRadius="lg" />
                    ))}
                  </VStack>
                ) : pagesData && pagesData.length > 0 ? (
                  <>
                  <TableContainer>
                    <Table variant="simple" size="sm">
                      <Thead>
                        <Tr>
                          <Th>Page URL</Th>
                          <Th isNumeric>Views</Th>
                          <Th isNumeric>% of Total</Th>
                          <Th isNumeric display={{ base: 'none', md: 'table-cell' }}>Unique</Th>
                          <Th isNumeric display={{ base: 'none', lg: 'table-cell' }}>Avg Time</Th>
                          <Th isNumeric display={{ base: 'none', lg: 'table-cell' }}>Total Time</Th>
                          <Th isNumeric display={{ base: 'none', xl: 'table-cell' }}>Entrances</Th>
                          <Th isNumeric display={{ base: 'none', xl: 'table-cell' }}>Exit Rate</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {paginatedPagesTabData.map((page) => (
                          <Tr key={page.path} _hover={{ bg: hoverBg }} transition="all 0.2s">
                            <Td>
                              <Text fontWeight="500" color="brand.500" isTruncated maxW="300px" title={page.path}>
                                {page.path}
                              </Text>
                            </Td>
                            <Td isNumeric fontWeight="600">{formatFullNumber(page.views)}</Td>
                            <Td isNumeric>
                              <Badge colorScheme="brand" variant="subtle" borderRadius="full">
                                {page.percentage}%
                              </Badge>
                            </Td>
                            <Td isNumeric display={{ base: 'none', md: 'table-cell' }}>{formatFullNumber(page.uniqueViews)}</Td>
                            <Td isNumeric display={{ base: 'none', lg: 'table-cell' }}>{page.avgTime ? formatDuration(page.avgTime) : '-'}</Td>
                            <Td isNumeric display={{ base: 'none', lg: 'table-cell' }}>{page.totalTime ? formatDuration(page.totalTime) : '-'}</Td>
                            <Td isNumeric display={{ base: 'none', xl: 'table-cell' }}>{formatFullNumber(page.entrances)}</Td>
                            <Td isNumeric display={{ base: 'none', xl: 'table-cell' }}>
                              <Badge colorScheme={page.exitRate > 50 ? 'red' : 'green'} variant="subtle" borderRadius="full">
                                {page.exitRate}%
                              </Badge>
                            </Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </TableContainer>
                  {totalPagesTabPages > 1 && (
                    <Flex justify="space-between" align="center" mt={4} pt={4} borderTop="1px solid" borderColor={borderColor}>
                      <Text fontSize="sm" color={textSecondary}>
                        Showing {((pagesTabPage - 1) * TAB_ITEMS_PER_PAGE) + 1}-{Math.min(pagesTabPage * TAB_ITEMS_PER_PAGE, filteredPagesTabData.length)} of {filteredPagesTabData.length}
                      </Text>
                      <HStack spacing={2}>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setPagesTabPage(p => Math.max(1, p - 1))}
                          isDisabled={pagesTabPage === 1}
                        >
                          Previous
                        </Button>
                        <Text fontSize="sm" color={textSecondary} minW="80px" textAlign="center">
                          Page {pagesTabPage} of {totalPagesTabPages}
                        </Text>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setPagesTabPage(p => Math.min(totalPagesTabPages, p + 1))}
                          isDisabled={pagesTabPage === totalPagesTabPages}
                        >
                          Next
                        </Button>
                      </HStack>
                    </Flex>
                  )}
                  </>
                ) : (
                  <Text color={textSecondary} textAlign="center" py={8}>
                    No page data available for this period
                  </Text>
                )}
              </CardBody>
            </Card>
          </TabPanel>

          {/* Landing Pages Tab */}
          <TabPanel px={0}>
            <Card bg={cardBg} border="1px solid" borderColor={borderColor}>
              <CardBody p={{ base: 4, md: 6 }}>
                <Flex justify="space-between" align="center" mb={4} flexWrap="wrap" gap={3}>
                  <HStack spacing={2}>
                    <Box p={2} bgGradient="linear(135deg, green.500 0%, teal.500 100%)" borderRadius="lg">
                      <Icon color="white" boxSize={4}>
                        <FontAwesomeIcon icon={faSignInAlt} />
                      </Icon>
                    </Box>
                    <Box>
                      <Heading size="md">Landing Pages</Heading>
                      <Text fontSize="sm" color={textSecondary}>Pages where sessions begin (entry points)</Text>
                    </Box>
                  </HStack>
                  <HStack spacing={3}>
                    <InputGroup size="sm" maxW="250px">
                      <InputLeftElement>
                        <Icon color={textSecondary} boxSize={3}>
                          <FontAwesomeIcon icon={faSearch} />
                        </Icon>
                      </InputLeftElement>
                      <Input
                        placeholder="Search landing pages..."
                        value={landingPagesSearch}
                        onChange={(e) => setLandingPagesSearch(e.target.value)}
                        borderRadius="lg"
                      />
                    </InputGroup>
                    {landingPagesData?.totalSessions > 0 && (
                      <Badge colorScheme="green" variant="subtle" borderRadius="full">
                        {formatFullNumber(landingPagesData.totalSessions)} total sessions
                      </Badge>
                    )}
                  </HStack>
                </Flex>
                {landingPagesError ? (
                  <Alert status="error" borderRadius="xl">
                    <AlertIcon />
                    {landingPagesError}
                  </Alert>
                ) : landingPagesLoading ? (
                  <VStack spacing={4} align="stretch">
                    {[1, 2, 3, 4].map((i) => (
                      <Skeleton key={i} height="40px" borderRadius="lg" />
                    ))}
                  </VStack>
                ) : landingPagesData?.data && landingPagesData.data.length > 0 ? (
                  <TableContainer>
                    <Table variant="simple" size="sm">
                      <Thead>
                        <Tr>
                          <Th>Landing Page</Th>
                          <Th isNumeric>Sessions</Th>
                          <Th isNumeric>% of Entries</Th>
                          <Th isNumeric display={{ base: 'none', md: 'table-cell' }}>Visitors</Th>
                          <Th isNumeric display={{ base: 'none', lg: 'table-cell' }}>Bounce Rate</Th>
                          <Th isNumeric display={{ base: 'none', lg: 'table-cell' }}>Avg Duration</Th>
                          <Th isNumeric display={{ base: 'none', xl: 'table-cell' }}>Pages/Session</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {landingPagesData.data
                          .filter(page => !landingPagesSearch || extractPath(page.url).toLowerCase().includes(landingPagesSearch.toLowerCase()))
                          .map((page) => (
                          <Tr key={page.url} _hover={{ bg: hoverBg }} transition="all 0.2s">
                            <Td>
                              <Text fontWeight="500" color="green.500" isTruncated maxW="300px" title={page.url}>
                                {extractPath(page.url)}
                              </Text>
                            </Td>
                            <Td isNumeric fontWeight="600">{formatFullNumber(page.sessions)}</Td>
                            <Td isNumeric>
                              <Badge colorScheme="green" variant="subtle" borderRadius="full">
                                {page.percentage}%
                              </Badge>
                            </Td>
                            <Td isNumeric display={{ base: 'none', md: 'table-cell' }}>{formatFullNumber(page.visitors)}</Td>
                            <Td isNumeric display={{ base: 'none', lg: 'table-cell' }}>
                              <Badge colorScheme={page.bounceRate > 50 ? 'red' : 'green'} variant="subtle" borderRadius="full">
                                {page.bounceRate}%
                              </Badge>
                            </Td>
                            <Td isNumeric display={{ base: 'none', lg: 'table-cell' }}>{formatDuration(page.avgDuration)}</Td>
                            <Td isNumeric display={{ base: 'none', xl: 'table-cell' }}>{page.avgPageviews}</Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Box py={8} textAlign="center" bg={hoverBg} borderRadius="xl">
                    <Icon color={textSecondary} boxSize={8} mb={2}>
                      <FontAwesomeIcon icon={faSignInAlt} />
                    </Icon>
                    <Text color={textSecondary}>No landing page data available for this period</Text>
                  </Box>
                )}
              </CardBody>
            </Card>
          </TabPanel>

          {/* Exit Pages Tab */}
          <TabPanel px={0}>
            <Card bg={cardBg} border="1px solid" borderColor={borderColor}>
              <CardBody p={{ base: 4, md: 6 }}>
                <Flex justify="space-between" align="center" mb={4} flexWrap="wrap" gap={3}>
                  <HStack spacing={2}>
                    <Box p={2} bgGradient="linear(135deg, red.500 0%, orange.500 100%)" borderRadius="lg">
                      <Icon color="white" boxSize={4}>
                        <FontAwesomeIcon icon={faSignOutAlt} />
                      </Icon>
                    </Box>
                    <Box>
                      <Heading size="md">Exit Pages</Heading>
                      <Text fontSize="sm" color={textSecondary}>Pages where sessions end (last page viewed)</Text>
                    </Box>
                  </HStack>
                  <HStack spacing={3}>
                    <InputGroup size="sm" maxW="250px">
                      <InputLeftElement>
                        <Icon color={textSecondary} boxSize={3}>
                          <FontAwesomeIcon icon={faSearch} />
                        </Icon>
                      </InputLeftElement>
                      <Input
                        placeholder="Search exit pages..."
                        value={exitPagesSearch}
                        onChange={(e) => setExitPagesSearch(e.target.value)}
                        borderRadius="lg"
                      />
                    </InputGroup>
                    {exitPagesData?.totalExits > 0 && (
                      <Badge colorScheme="red" variant="subtle" borderRadius="full">
                        {formatFullNumber(exitPagesData.totalExits)} total exits
                      </Badge>
                    )}
                  </HStack>
                </Flex>
                {exitPagesError ? (
                  <Alert status="error" borderRadius="xl">
                    <AlertIcon />
                    {exitPagesError}
                  </Alert>
                ) : exitPagesLoading ? (
                  <VStack spacing={4} align="stretch">
                    {[1, 2, 3, 4].map((i) => (
                      <Skeleton key={i} height="40px" borderRadius="lg" />
                    ))}
                  </VStack>
                ) : exitPagesData?.data && exitPagesData.data.length > 0 ? (
                  <TableContainer>
                    <Table variant="simple" size="sm">
                      <Thead>
                        <Tr>
                          <Th>Exit Page</Th>
                          <Th isNumeric>Exits</Th>
                          <Th isNumeric>% of Exits</Th>
                          <Th isNumeric display={{ base: 'none', md: 'table-cell' }}>Pageviews</Th>
                          <Th isNumeric display={{ base: 'none', lg: 'table-cell' }}>Exit Rate</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {exitPagesData.data
                          .filter(page => !exitPagesSearch || extractPath(page.url).toLowerCase().includes(exitPagesSearch.toLowerCase()))
                          .map((page) => (
                          <Tr key={page.url} _hover={{ bg: hoverBg }} transition="all 0.2s">
                            <Td>
                              <Text fontWeight="500" color="red.500" isTruncated maxW="300px" title={page.url}>
                                {extractPath(page.url)}
                              </Text>
                            </Td>
                            <Td isNumeric fontWeight="600">{formatFullNumber(page.exits)}</Td>
                            <Td isNumeric>
                              <Badge colorScheme="red" variant="subtle" borderRadius="full">
                                {page.percentage}%
                              </Badge>
                            </Td>
                            <Td isNumeric display={{ base: 'none', md: 'table-cell' }}>{formatFullNumber(page.pageviews)}</Td>
                            <Td isNumeric display={{ base: 'none', lg: 'table-cell' }}>
                              <Badge colorScheme={page.exitRate > 50 ? 'red' : 'orange'} variant="subtle" borderRadius="full">
                                {page.exitRate}%
                              </Badge>
                            </Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Box py={8} textAlign="center" bg={hoverBg} borderRadius="xl">
                    <Icon color={textSecondary} boxSize={8} mb={2}>
                      <FontAwesomeIcon icon={faSignOutAlt} />
                    </Icon>
                    <Text color={textSecondary}>No exit page data available for this period</Text>
                  </Box>
                )}
              </CardBody>
            </Card>
          </TabPanel>

          {/* Sources Tab */}
          <TabPanel px={0}>
            <Card bg={cardBg} border="1px solid" borderColor={borderColor}>
              <CardBody p={{ base: 4, md: 6 }}>
                <Flex justify="space-between" align="center" mb={4}>
                  <HStack spacing={2}>
                    <Box p={2} bgGradient="linear(135deg, orange.500 0%, red.500 100%)" borderRadius="lg">
                      <Icon color="white" boxSize={4}>
                        <FontAwesomeIcon icon={faGlobe} />
                      </Icon>
                    </Box>
                    <Box>
                      <Heading size="md">Traffic Sources</Heading>
                      <Text fontSize="sm" color={textSecondary}>Where your visitors are coming from</Text>
                    </Box>
                  </HStack>
                </Flex>
                {sourcesError ? (
                  <Alert status="error" borderRadius="xl">
                    <AlertIcon />
                    {sourcesError}
                  </Alert>
                ) : sourcesLoading ? (
                  <VStack spacing={4} align="stretch">
                    {[1, 2, 3, 4, 5].map((i) => (
                      <Skeleton key={i} height="60px" borderRadius="xl" />
                    ))}
                  </VStack>
                ) : sourcesData && sourcesData.length > 0 ? (
                  <VStack spacing={4} align="stretch">
                    <Text fontSize="sm" color={textSecondary} mb={2}>
                      Click on categories with details to expand and see specific sources or keywords
                    </Text>
                    {sourcesData.map((source) => (
                      <SourceCategory
                        key={source.type || source.name}
                        source={source}
                        hoverBg={hoverBg}
                        borderColor={borderColor}
                        textSecondary={textSecondary}
                      />
                    ))}
                  </VStack>
                ) : (
                  <Text color={textSecondary} textAlign="center" py={8}>
                    No traffic source data available for this period
                  </Text>
                )}
              </CardBody>
            </Card>
          </TabPanel>

          {/* Geography Tab */}
          <TabPanel px={0}>
            <Card bg={cardBg} border="1px solid" borderColor={borderColor}>
              <CardBody>
                <Flex justify="space-between" align="center" mb={4}>
                  <HStack spacing={2}>
                    <Box p={2} bgGradient="linear(135deg, cyan.500 0%, blue.500 100%)" borderRadius="lg">
                      <Icon color="white" boxSize={4}>
                        <FontAwesomeIcon icon={faMapMarkerAlt} />
                      </Icon>
                    </Box>
                    <Heading size="md">Geographic Distribution</Heading>
                  </HStack>
                  {geoLoading && <Spinner size="sm" color="brand.500" />}
                </Flex>
                {geoError ? (
                  <Alert status="error" borderRadius="xl">
                    <AlertIcon />
                    {geoError}
                  </Alert>
                ) : geoData && geoData.length > 0 ? (
                  <VStack spacing={4} align="stretch">
                    {geoData.slice(0, 10).map((item) => (
                      <Box
                        key={item.country || item.country_code}
                        p={4}
                        bg={hoverBg}
                        borderRadius="xl"
                        _hover={{ transform: 'translateX(4px)' }}
                        transition="all 0.2s"
                      >
                        <Flex justify="space-between" align="center" mb={2}>
                          <HStack spacing={3}>
                            <Text fontSize="lg">{item.flag || '🌍'}</Text>
                            <Text fontWeight="600">{item.country || item.country_name || 'Unknown'}</Text>
                          </HStack>
                          <HStack spacing={4}>
                            <Text fontSize="sm" color={textSecondary}>
                              <Text as="span" fontWeight="600" color="inherit">
                                {formatFullNumber(item.visitors || item.unique_visitors)}
                              </Text> visitors
                            </Text>
                            <Badge colorScheme="cyan" variant="subtle" borderRadius="full">
                              {item.percentage || item.percent || 0}%
                            </Badge>
                          </HStack>
                        </Flex>
                        <Progress
                          value={item.percentage || item.percent || 0}
                          size="sm"
                          colorScheme="cyan"
                          borderRadius="full"
                          bg={borderColor}
                        />
                      </Box>
                    ))}
                  </VStack>
                ) : (
                  <Box h="300px" display="flex" alignItems="center" justifyContent="center" bg={hoverBg} borderRadius="xl" border="2px dashed" borderColor={borderColor}>
                    <VStack spacing={2}>
                      <Icon color={textSecondary} boxSize={8}>
                        <FontAwesomeIcon icon={faGlobe} />
                      </Icon>
                      <Text color={textSecondary} fontWeight="500">
                        {geoLoading ? 'Loading geographic data...' : 'No geographic data available'}
                      </Text>
                    </VStack>
                  </Box>
                )}
              </CardBody>
            </Card>
          </TabPanel>

          {/* Devices Tab */}
          <TabPanel px={0}>
            <Card bg={cardBg} border="1px solid" borderColor={borderColor}>
              <CardBody p={{ base: 4, md: 6 }}>
                <Flex justify="space-between" align="center" mb={4}>
                  <HStack spacing={2}>
                    <Box p={2} bgGradient="linear(135deg, brand.500 0%, purple.500 100%)" borderRadius="lg">
                      <Icon color="white" boxSize={4}>
                        <FontAwesomeIcon icon={faDesktop} />
                      </Icon>
                    </Box>
                    <Box>
                      <Heading size="md">Devices</Heading>
                      <Text fontSize="sm" color={textSecondary}>Breakdown by device type</Text>
                    </Box>
                  </HStack>
                </Flex>
                {devicesError ? (
                  <Alert status="error" borderRadius="xl">
                    <AlertIcon />
                    {devicesError}
                  </Alert>
                ) : devicesLoading ? (
                  <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6}>
                    {[1, 2, 3].map((i) => (
                      <Box key={i} p={6} bg={hoverBg} borderRadius="xl" textAlign="center">
                        <Skeleton height="80px" width="80px" mx="auto" mb={4} borderRadius="2xl" />
                        <SkeletonText noOfLines={3} spacing={2} />
                      </Box>
                    ))}
                  </SimpleGrid>
                ) : devicesData && devicesData.length > 0 ? (
                  <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6}>
                    {devicesData.map((device) => {
                      const deviceType = (device.device || device.device_type || 'desktop').toLowerCase();
                      const color = deviceColorMap[deviceType] || 'gray';
                      const icon = deviceIconMap[deviceType] || faDesktop;
                      return (
                        <Box
                          key={device.device || device.device_type}
                          p={6}
                          bg={hoverBg}
                          borderRadius="xl"
                          textAlign="center"
                          _hover={{ transform: 'translateY(-1px)' }}
                          transition="all 0.2s"
                        >
                          <Box
                            w="80px"
                            h="80px"
                            mx="auto"
                            mb={4}
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            bg={`${color}.100`}
                            borderRadius="2xl"
                          >
                            <Icon color={`${color}.500`} boxSize={8}>
                              <FontAwesomeIcon icon={icon} />
                            </Icon>
                          </Box>
                          <Text fontWeight="600" fontSize="lg" mb={1} textTransform="capitalize">
                            {device.device || device.device_type}
                          </Text>
                          <Text
                            fontSize="3xl"
                            fontWeight="700"
                            bgGradient={`linear(to-r, ${color}.500, ${color}.600)`}
                            bgClip="text"
                          >
                            {formatShare(device.percentage || device.percent, device.visitors || device.unique_visitors)}
                          </Text>
                          <Text color={textSecondary} fontSize="sm">
                            {formatFullNumber(device.visitors || device.unique_visitors)} visitors
                          </Text>
                        </Box>
                      );
                    })}
                  </SimpleGrid>
                ) : (
                  <Text color={textSecondary} textAlign="center" py={8}>
                    No device data available for this period
                  </Text>
                )}
              </CardBody>
            </Card>
          </TabPanel>

          {/* Engagement Tab */}
          <TabPanel px={0}>
            {/* User Activity Over Time */}
            <Card bg={cardBg} border="1px solid" borderColor={borderColor} mb={6}>
              <CardBody>
                <Flex justify="space-between" align="center" mb={4}>
                  <HStack spacing={2}>
                    <Box p={2} bgGradient="linear(135deg, purple.500 0%, blue.500 100%)" borderRadius="lg">
                      <Icon color="white" boxSize={4}>
                        <FontAwesomeIcon icon={faClock} />
                      </Icon>
                    </Box>
                    <Box>
                      <Heading size="md">User Activity by Hour</Heading>
                      <Text fontSize="sm" color={textSecondary}>When your users are most active</Text>
                    </Box>
                  </HStack>
                  {hourlyData?.peakHours && hourlyData.peakHours.length > 0 && (
                    <Badge colorScheme="purple" variant="subtle" borderRadius="full">
                      Peak: {hourlyData.peakHours.map(h => `${h.toString().padStart(2, '0')}:00`).join(', ')}
                    </Badge>
                  )}
                </Flex>
                {hourlyError ? (
                  <Alert status="error" borderRadius="xl">
                    <AlertIcon />
                    {hourlyError}
                  </Alert>
                ) : hourlyLoading ? (
                  <Skeleton h="300px" borderRadius="xl" />
                ) : hourlyData?.data && hourlyData.data.length > 0 ? (
                  <Box h="300px">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={hourlyData.data} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
                        <XAxis dataKey="label" tick={{ fontSize: 10, fill: axisTextColor }} axisLine={{ stroke: gridColor }} tickLine={{ stroke: gridColor }} />
                        <YAxis tick={{ fontSize: 12, fill: axisTextColor }} axisLine={{ stroke: gridColor }} tickLine={{ stroke: gridColor }} />
                        <RechartsTooltip contentStyle={{ backgroundColor: tooltipBg, color: tooltipText, border: `1px solid ${gridColor}`, borderRadius: '8px' }} cursor={{ fill: 'transparent' }} />
                        <Bar {...hourlyAnim} dataKey="pageviews" name="Pageviews" fill={brandColor} radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </Box>
                ) : (
                  <Box h="300px" display="flex" alignItems="center" justifyContent="center" bg={hoverBg} borderRadius="xl">
                    <Text color={textSecondary}>No hourly activity data available</Text>
                  </Box>
                )}
              </CardBody>
            </Card>

            {/* User Stickiness */}
            <Card bg={cardBg} border="1px solid" borderColor={borderColor}>
              <CardBody>
                <Flex justify="space-between" align="center" mb={4}>
                  <HStack spacing={2}>
                    <Box p={2} bgGradient="linear(135deg, teal.500 0%, green.500 100%)" borderRadius="lg">
                      <Icon color="white" boxSize={4}>
                        <FontAwesomeIcon icon={faUserClock} />
                      </Icon>
                    </Box>
                    <Box>
                      <Heading size="md">User Stickiness</Heading>
                      <Text fontSize="sm" color={textSecondary}>How often users return (DAU/WAU/MAU ratios)</Text>
                    </Box>
                  </HStack>
                </Flex>
                {stickinessError ? (
                  <Alert status="error" borderRadius="xl">
                    <AlertIcon />
                    {stickinessError}
                  </Alert>
                ) : stickinessLoading ? (
                  <SimpleGrid columns={{ base: 2, md: 3, lg: 6 }} spacing={4}>
                    {[1, 2, 3, 4, 5, 6].map(i => <Skeleton key={i} h="100px" borderRadius="lg" />)}
                  </SimpleGrid>
                ) : stickinessData ? (
                  <SimpleGrid columns={{ base: 2, md: 3, lg: 6 }} spacing={4}>
                    <Card bg={hoverBg} border="1px solid" borderColor={borderColor}>
                      <CardBody py={4} textAlign="center">
                        <Text fontSize="xs" color={textSecondary} fontWeight="500" textTransform="uppercase">
                          DAU (Avg)
                          <Tooltip label="Average Daily Active Users over the last 7 days" placement="top">
                            <span style={{ marginLeft: '4px', cursor: 'help' }}>
                              <FontAwesomeIcon icon={faInfoCircle} size="xs" />
                            </span>
                          </Tooltip>
                        </Text>
                        <Text fontSize="2xl" fontWeight="700" color="teal.500">
                          {formatFullNumber(stickinessData.dau)}
                        </Text>
                      </CardBody>
                    </Card>
                    <Card bg={hoverBg} border="1px solid" borderColor={borderColor}>
                      <CardBody py={4} textAlign="center">
                        <Text fontSize="xs" color={textSecondary} fontWeight="500" textTransform="uppercase">
                          WAU
                          <Tooltip label="Weekly Active Users (unique visitors in last 7 days)" placement="top">
                            <span style={{ marginLeft: '4px', cursor: 'help' }}>
                              <FontAwesomeIcon icon={faInfoCircle} size="xs" />
                            </span>
                          </Tooltip>
                        </Text>
                        <Text fontSize="2xl" fontWeight="700" color="blue.500">
                          {formatFullNumber(stickinessData.wau)}
                        </Text>
                      </CardBody>
                    </Card>
                    <Card bg={hoverBg} border="1px solid" borderColor={borderColor}>
                      <CardBody py={4} textAlign="center">
                        <Text fontSize="xs" color={textSecondary} fontWeight="500" textTransform="uppercase">
                          MAU
                          <Tooltip label="Monthly Active Users (unique visitors in last 30 days)" placement="top">
                            <span style={{ marginLeft: '4px', cursor: 'help' }}>
                              <FontAwesomeIcon icon={faInfoCircle} size="xs" />
                            </span>
                          </Tooltip>
                        </Text>
                        <Text fontSize="2xl" fontWeight="700" color="purple.500">
                          {formatFullNumber(stickinessData.mau)}
                        </Text>
                      </CardBody>
                    </Card>
                    <Card bg={hoverBg} border="1px solid" borderColor={borderColor}>
                      <CardBody py={4} textAlign="center">
                        <Text fontSize="xs" color={textSecondary} fontWeight="500" textTransform="uppercase">
                          DAU/WAU
                          <Tooltip label="Daily stickiness: Higher means users visit more frequently within a week" placement="top">
                            <span style={{ marginLeft: '4px', cursor: 'help' }}>
                              <FontAwesomeIcon icon={faInfoCircle} size="xs" />
                            </span>
                          </Tooltip>
                        </Text>
                        <Text fontSize="2xl" fontWeight="700" color={stickinessData.dauWauRatio > 50 ? 'green.500' : stickinessData.dauWauRatio > 25 ? 'orange.500' : 'red.500'}>
                          {stickinessData.dauWauRatio}%
                        </Text>
                        <Badge colorScheme={stickinessData.interpretation?.dauWau === 'High' ? 'green' : stickinessData.interpretation?.dauWau === 'Medium' ? 'orange' : 'red'} variant="subtle" borderRadius="full" fontSize="xs">
                          {stickinessData.interpretation?.dauWau} Engagement
                        </Badge>
                      </CardBody>
                    </Card>
                    <Card bg={hoverBg} border="1px solid" borderColor={borderColor}>
                      <CardBody py={4} textAlign="center">
                        <Text fontSize="xs" color={textSecondary} fontWeight="500" textTransform="uppercase">
                          DAU/MAU
                          <Tooltip label="Monthly stickiness: Higher means users visit more frequently within a month" placement="top">
                            <span style={{ marginLeft: '4px', cursor: 'help' }}>
                              <FontAwesomeIcon icon={faInfoCircle} size="xs" />
                            </span>
                          </Tooltip>
                        </Text>
                        <Text fontSize="2xl" fontWeight="700" color={stickinessData.dauMauRatio > 20 ? 'green.500' : stickinessData.dauMauRatio > 10 ? 'orange.500' : 'red.500'}>
                          {stickinessData.dauMauRatio}%
                        </Text>
                        <Badge colorScheme={stickinessData.interpretation?.dauMau === 'High' ? 'green' : stickinessData.interpretation?.dauMau === 'Medium' ? 'orange' : 'red'} variant="subtle" borderRadius="full" fontSize="xs">
                          {stickinessData.interpretation?.dauMau} Retention
                        </Badge>
                      </CardBody>
                    </Card>
                    <Card bg={hoverBg} border="1px solid" borderColor={borderColor}>
                      <CardBody py={4} textAlign="center">
                        <Text fontSize="xs" color={textSecondary} fontWeight="500" textTransform="uppercase">
                          WAU/MAU
                          <Tooltip label="Weekly retention: Percentage of monthly users who visited in the last week" placement="top">
                            <span style={{ marginLeft: '4px', cursor: 'help' }}>
                              <FontAwesomeIcon icon={faInfoCircle} size="xs" />
                            </span>
                          </Tooltip>
                        </Text>
                        <Text fontSize="2xl" fontWeight="700" color="cyan.500">
                          {stickinessData.wauMauRatio}%
                        </Text>
                      </CardBody>
                    </Card>
                  </SimpleGrid>
                ) : (
                  <Box py={8} textAlign="center" bg={hoverBg} borderRadius="xl">
                    <Text color={textSecondary}>No stickiness data available</Text>
                  </Box>
                )}
              </CardBody>
            </Card>
          </TabPanel>
        </TabPanels>
      </Tabs>

      {/* Export Builder Modal */}
      <ExportBuilderModal
        isOpen={isExportOpen}
        onClose={onExportClose}
        pageType="analytics"
        data={{
          overview: overviewData,
          trafficData,
          pagesData,
          sourcesData,
          geoData,
          devicesData,
          landingPages: landingPagesData?.data,
          exitPages: exitPagesData?.data,
        }}
        dateRange={dateRange}
      />
    </Box>
  );
}

export default AnalyticsPage;
